'use client';

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faMapLocationDot,
  faPlus,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { useSelector } from 'react-redux';
import { selectDiscoveryMapCurrentTerritory } from 'src/redux/slices/discoveryMapSlice';
import { useGetAllTerritoriesQuery } from 'src/api/redux';
import useSelectDiscoveryTerritory from 'src/hooks/discovery/useSelectDiscoveryTerritory';
import { NO_TERRITORY_ACTIVE_SELECTION } from 'src/components/TerritoryPanel/constants';
import {
  TerritoryFolder,
  TerritoryPanelItem,
} from '../TerritoryPanel/components';
import { TerritoryPanelProvider } from '../TerritoryPanel/context/TerritoryPanelContext';
import { Drawer, CircularProgress } from 'src/components';
import { Search } from 'src/components/PulseUI';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { CopyTerritoryModal } from '../CopyTerritoryModal';
import { useDebounce } from 'use-debounce';
import { useRouter } from 'next/navigation';

const TerritoryDrawer = () => {
  const log = useLogging();
  const { user } = useAuth();

  const auth = useAuthorization();
  const router = useRouter();
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const selectTerritory = useSelectDiscoveryTerritory();

  const isOpen = isDrawerOpen('discovery-territory-drawer');

  const [expandedReps, setExpandedReps] = useState<string[]>([]);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [territoryToCopy, setTerritoryToCopy] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isMedScoutUser =
    user?.is_superuser || user?.permissions?.is_support_user;

  // Use RTK Query hook with search parameter
  const {
    data: myTerritoryData,
    isLoading,
    isFetching,
  } = useGetAllTerritoriesQuery({
    search: debouncedSearch || undefined,
  });

  const canUserWrite = useMemo(() => {
    return auth.canWrite('territory', '*');
  }, [auth]);

  const { specialTerritories, myTerritories, repTerritoryGroups } =
    useMemo(() => {
      if (!myTerritoryData?.length)
        return {
          specialTerritories: [],
          myTerritories: [],
          repTerritoryGroups: {},
        };

      const searchLower = debouncedSearch.toLowerCase();

      // Special territories
      const specialTerritories = [
        {
          id: NO_TERRITORY_ACTIVE_SELECTION.id,
          value: NO_TERRITORY_ACTIVE_SELECTION.id,
          name: NO_TERRITORY_ACTIVE_SELECTION.name,
          icon: faMapLocationDot,
          polygons: [],
          owner: user,
          is_canonical: false,
        } as unknown as MedScout.Territory,
      ];

      // My territories
      const myTerritories = myTerritoryData
        .filter((territory) => {
          const matchesSearch =
            !debouncedSearch ||
            territory.name.toLowerCase().includes(searchLower) ||
            `${territory.owner.first_name} ${territory.owner.last_name}`
              .toLowerCase()
              .includes(searchLower);
          return territory.owner?.id === user?.id && matchesSearch;
        })
        .sort((a, b) => a.name.localeCompare(b.name));

      // Group rep territories by rep name
      const repGroups = myTerritoryData
        .filter((territory) => {
          const matchesSearch =
            !debouncedSearch ||
            territory.name.toLowerCase().includes(searchLower) ||
            `${territory.owner.first_name} ${territory.owner.last_name}`
              .toLowerCase()
              .includes(searchLower);
          return territory.owner?.id !== user?.id && matchesSearch;
        })
        .reduce((acc, territory) => {
          const repName = `${territory.owner.first_name} ${territory.owner.last_name}`;
          if (!acc[repName]) acc[repName] = [];
          acc[repName].push(territory);
          return acc;
        }, {} as Record<string, typeof myTerritoryData>);

      // Sort territories within each rep group
      Object.keys(repGroups).forEach((repName) => {
        repGroups[repName].sort((a, b) => {
          if (a.is_canonical !== b.is_canonical) return a.is_canonical ? -1 : 1;
          return a.name.localeCompare(b.name);
        });
      });

      return {
        specialTerritories,
        myTerritories,
        repTerritoryGroups: repGroups,
      };
    }, [myTerritoryData, user, debouncedSearch]);

  useEffect(() => {
    if (isOpen && currentTerritory) {
      // Only expand folder if the selected territory belongs to another user (not the current user)
      if (currentTerritory.owner?.id !== user?.id) {
        const repName = `${currentTerritory.owner.first_name} ${currentTerritory.owner.last_name}`;
        setExpandedReps((prev) => {
          if (!prev.includes(repName)) {
            return [...prev, repName];
          }
          return prev;
        });
      }
    }
  }, [isOpen, currentTerritory, user?.id]);

  const handleToggle = (repName: string) => {
    setExpandedReps((prev) =>
      prev.includes(repName)
        ? prev.filter((name) => name !== repName)
        : [...prev, repName]
    );
  };

  const handleSelect = (territory: any) => {
    if (territory.id === NO_TERRITORY_ACTIVE_SELECTION.id) {
      log.event('useMapWithNoTerritorySelected');
      selectTerritory(null);
      setSearch('');
      setExpandedReps([]);
      handleClose();
      return;
    }

    // First expand the rep's section if needed
    const repName = `${territory.owner.first_name} ${territory.owner.last_name}`;
    setExpandedReps((prev) => {
      if (!prev.includes(repName)) {
        return [...prev, repName];
      }
      return prev;
    });

    selectTerritory(territory);
    setSearch('');
    handleClose();
  };

  // Handle scrolling when drawer opens
  useEffect(() => {
    if (isOpen && currentTerritory && scrollContainerRef.current) {
      const timer = setTimeout(() => {
        const element = document.querySelector(
          `[data-territory-id="${currentTerritory.id}"]`
        ) as HTMLElement;
        if (element && scrollContainerRef.current) {
          // Calculate the element's position relative to the scroll container
          const container = scrollContainerRef.current;
          const elementRect = element.getBoundingClientRect();

          // Calculate scroll position to center the element
          const scrollTop =
            element.offsetTop -
            container.offsetTop -
            container.clientHeight / 2 +
            elementRect.height / 2;

          // Scroll within the container only
          container.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, currentTerritory]);

  const handleAddTerritory = () => {
    router.push('/territory-manager/create');
    handleClose();
  };

  const handleTerritoryToCopy = (territoryId: number) => {
    if (!territoryId) return;
    setShowCopyDialog(!showCopyDialog);
    setTerritoryToCopy(territoryId);
  };

  const handleCloseCopyDialog = () => {
    setShowCopyDialog(false);
    setTerritoryToCopy(null);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleClose = () => {
    closeDrawer('discovery-territory-drawer');
    setSearch('');
  };

  const handleCloseDropdown = () => {
    handleClose();
  };

  return (
    <TerritoryPanelProvider
      value={{
        expandedReps,
        selectedTerritory: currentTerritory,
        onToggle: handleToggle,
        onSelect: handleSelect,
      }}
    >
      <Drawer
        isOpen={isOpen}
        onClose={handleClose}
        anchor="left"
        size="base2"
        variant="persistent"
        className="z-[150] absolute left-0 top-0 bottom-0"
        data-testid="territory-drawer"
      >
        {/* Drawer Header */}
        <Drawer.Header
          className="bg-sky-100 text-primary border-b-0"
          data-testid="territory-drawer-header"
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <p
                className="font-bold text-lg"
                data-testid="territory-drawer-title"
              >
                Territories
              </p>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                variant="plain"
                color="neutral"
                size="sm"
                onClick={handleClose}
                icon={faXmark}
                className="text-primary hover:bg-sky-200"
                data-testid="territory-drawer-close-button"
              />
            </div>
          </div>
        </Drawer.Header>

        {/* Drawer Content */}
        <Drawer.Content
          className="bg-neutral-050"
          data-testid="territory-drawer-content"
        >
          <div className="h-full flex flex-col">
            <div className="p-4 border-b border-neutral-200 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h3
                  className="text-base font-semibold text-neutral-900"
                  data-testid="load-territory-label"
                >
                  Load Territory
                </h3>
                {(isMedScoutUser || canUserWrite) && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={handleAddTerritory}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleAddTerritory();
                      }
                    }}
                    className={`flex items-center gap-1.5 text-sm text-sky-600 hover:text-sky-600 cursor-pointer font-medium ${
                      !auth?.canWrite('territory', '*')
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    data-testid="add-territory-button"
                  >
                    <Icon icon={faPlus} className="w-3.5 h-3.5" />
                    Add Territory
                  </div>
                )}
              </div>
              <Search
                value={search}
                onSearch={handleSearch}
                placeholder="Search territories..."
                size="sm"
                className="w-full rounded-full"
                autoFocus
                data-testid="territory-search-input"
              />
            </div>

            <div
              ref={scrollContainerRef}
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
              data-no-scroll
              data-testid="territory-list-container"
            >
              {isLoading && !myTerritoryData ? (
                <div
                  className="w-full flex items-center justify-center px-4 py-8"
                  data-testid="loading-indicator"
                >
                  <CircularProgress size="sm" color="neutral" />
                </div>
              ) : (
                <>
                  <div className="divide-y divide-neutral-100">
                    {/* Special Territories */}
                    {specialTerritories.map((territory) => (
                      <div key={territory.id} className="w-full px-3">
                        <TerritoryPanelItem
                          territory={territory}
                          isSelected={!currentTerritory}
                          onSelect={handleSelect}
                          onCopy={handleTerritoryToCopy}
                          onClose={handleCloseDropdown}
                        />
                      </div>
                    ))}

                    {/* User's Territories - Direct List */}
                    {myTerritories.map((territory) => (
                      <div key={territory.id} className="w-full px-3">
                        <TerritoryPanelItem
                          territory={territory}
                          isSelected={currentTerritory?.id === territory.id}
                          onSelect={handleSelect}
                          onCopy={handleTerritoryToCopy}
                          onClose={handleCloseDropdown}
                        />
                      </div>
                    ))}

                    {/* Rep Territory Groups */}
                    {Object.entries(repTerritoryGroups)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([repName, territories]) => (
                        <TerritoryFolder
                          key={repName}
                          repName={repName}
                          territories={territories as typeof myTerritoryData}
                          onCopy={handleTerritoryToCopy}
                          onClose={handleCloseDropdown}
                        />
                      ))}
                  </div>

                  {/* Inline loading indicator during fetch (e.g., during search) */}
                  {isFetching && myTerritoryData && (
                    <div
                      className="w-full flex items-center justify-center px-4 py-2"
                      data-testid="fetching-indicator"
                    >
                      <CircularProgress size="sm" color="neutral" />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </Drawer.Content>
        <Drawer.Footer
          className="px-6 py-1 bg-white border-t border-neutral-300"
          data-testid="territory-drawer-footer"
        >
          <div className="w-full flex justify-center items-center">
            <span
              className="text-xs text-neutral-500"
              data-testid="territory-count"
            >
              {myTerritoryData?.length || 0} territories available
            </span>
          </div>
        </Drawer.Footer>
      </Drawer>
      <CopyTerritoryModal
        isOpen={showCopyDialog}
        onClose={handleCloseCopyDialog}
        territory={territoryToCopy}
      />
    </TerritoryPanelProvider>
  );
};

export default TerritoryDrawer;
